'use client';

import { useCallback, useState } from 'react';
import { Button, Icons } from '@/components';
import { useDropzone } from 'react-dropzone';

interface FileUploadProps {
    onFileContent: (content: string, name: string) => void;
}

export function FileUpload({ onFileContent }: FileUploadProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [fileInfo, setFileInfo] = useState<{
        name: string;
        size: number;
    } | null>(null);

    const handleFiles = useCallback(
        async (files: File[]) => {
            const readFileContent = (file: File): Promise<string> => {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (event) =>
                        resolve(event.target?.result as string);
                    reader.onerror = (error) => reject(error);
                    reader.readAsText(file);
                });
            };

            if (files.length > 0) {
                setIsLoading(true);
                const file = files[0];
                setFileInfo({
                    name: file.name,
                    size: file.size,
                });
                const content = await readFileContent(file);
                onFileContent(content, file.name);
                setIsLoading(false);
            }
        },
        [setFileInfo, onFileContent, setIsLoading]
    );

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            handleFiles(acceptedFiles);
        },
        [handleFiles]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: false,
    });

    const handleRemove = () => {
        setFileInfo(null);
        onFileContent('', '');
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return bytes + 'B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + 'kB';
        return (bytes / (1024 * 1024)).toFixed(2) + 'MB';
    };

    return (
        <div className="w-full">
            {fileInfo ? (
                <div className="flex items-center gap-2 p-2 bg-gray-100 border border-gray-200 rounded-lg max-w-xs">
                    <Icons.fileText className="w-4 h-4 text-gray-500" />
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                            {fileInfo.name}
                        </p>
                        <p className="text-xs text-gray-500">
                            {formatFileSize(fileInfo.size)}
                        </p>
                    </div>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={handleRemove}
                        aria-label="Remove"
                    >
                        <Icons.x className="h-4 w-4" />
                    </Button>
                </div>
            ) : (
                <div {...getRootProps()}>
                    <label htmlFor="file-upload" className="sr-only">
                        Upload file
                    </label>
                    <input
                        id="file-upload"
                        {...getInputProps()}
                        accept=".md,.txt"
                        data-testid="file-input"
                        aria-label="File upload"
                    />
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        disabled={isLoading}
                        aria-label={isLoading ? 'Loading' : 'Upload file'}
                    >
                        {isLoading ? (
                            <Icons.loader className="h-4 w-4 animate-spin" />
                        ) : (
                            <Icons.paperclip className="h-4 w-4" />
                        )}
                    </Button>
                </div>
            )}
        </div>
    );
}
